import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Accordion from "@mui/material/Accordion";
import Box from "@mui/material/Box";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import { Select, MenuItem } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { map, has } from "lodash";
import {
  MenuProps,
  multiplierOptions,
  multiplierMappings,
  DropdownSx,
  ENTITY_TYPES,
} from "../../utils";
import ItemTable from "./ItemTable";
import PogTable from "./PogTable";
import ExportButton from "../../components/ExportButton";
import { validItem } from "../../utils/itemValidator";
import useStableCallback from "../../hooks/useStableCallback";
import { setAdjQtyError, setPrevAdjQty } from "../../store/globalSlice";

const ToolbarSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: "0 5px",
};

const VAccordionSx = { border: "1px groove #9e9e9e63" };

const VAccordionSummarySx = {
  "& .MuiAccordionSummary-content": {
    justifyContent: "space-between",
    alignItems: "center",
    margin: "5px 0",
    "& .sections": {
      display: "fex",
      alignItems: "center",
      mr: 1,
    },
  },
};

const PogTableWrapperSx = {
  mb: 1,
  padding: "4px 4px",
  backgroundColor: "#d8d3c5",
  borderRadius: 1.5,
  border: "1px solid lightgrey",
};

const ItemTableWrapperSx = {
  padding: "4px 4px",
  backgroundColor: "#e5d090",
  border: "1px solid lightgrey",
  borderRadius: 1.5,
};

const VAccordionTransitionProps = {
  unmountOnExit: true,
  timeout: 0,
};

const VSubTitleSx = { fontSize: 14, ml: 1 };

const MultiplierSx = { minWidth: 120, m: 0, p: 0 };

const SubTitleSx = { fontSize: 16 };

const VirtualizedAccordionRow = ({
  row,
  index,
  setSize,
  windowWidth,
  scope,
  parentId,
  subRows,
  getProcessedRow,
  serverSelections,
  updateSelections,
  retailerNotes,
  plannerNotes,
}) => {
  const dispatch = useDispatch();
  const entityType = useSelector((store) => store.global.settings.entityType);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const prevAdjQty = useSelector((store) => store.global.prevAdjQty);
  const restrictedPogs = useSelector(
    (store) => store.global.settings.restrictedPogs
  );
  const [expand, setExpand] = useState(false);
  const rowRef = useRef();

  useEffect(() => {
    setSize(index, rowRef.current.getBoundingClientRect().height);
  }, [windowWidth, index, setSize]);

  useEffect(() => {
    //Auto close accordion so if adj qty error then reset its value.
    if (!expand && adjQtyError) {
      const isFound = subRows.find((p) => p.id === adjQtyError);
      if (isFound) {
        setTimeout(() => {
          getProcessedRow({ id: adjQtyError, adjqty: prevAdjQty });
          dispatch(setAdjQtyError(null));
          dispatch(setPrevAdjQty(null));
        });
      }
    }
  }, [expand, adjQtyError, dispatch, subRows, getProcessedRow, prevAdjQty]);

  const updateSize = (e, exp) => {
    if (!exp && adjQtyError) {
      const isFound = subRows.find((p) => p.id === adjQtyError);
      if (isFound) {
        getProcessedRow({ id: adjQtyError, adjqty: prevAdjQty });
        dispatch(setAdjQtyError(null));
        dispatch(setPrevAdjQty(null));
      }
    }
    setTimeout(function () {
      setSize(index, rowRef.current.getBoundingClientRect().height);
    });
  };

  const currentTitle = useMemo(() => {
    return `${row[index]?.PlanogramName} - ${row[index]?.PlanogramNumber}`;
  }, [row, index]);

  const currentSelections = useMemo(
    () => serverSelections ?? [],
    [serverSelections]
  );

  const handleAccordionRowSelection = useStableCallback((event) => {
    const { checked } = event?.target;
    const currentParentId = parentId;
    let currentSelections = [];
    if (checked) {
      const itemIds = subRows;
      let itemSelectionIds = [];
      if (ENTITY_TYPES.orders === entityType) {
        itemSelectionIds = map(itemIds, validItem).filter((i) => !!i);
      } else {
        itemSelectionIds = map(itemIds, "id");
      }
      currentSelections = [currentParentId, ...itemSelectionIds];
    } else {
      currentSelections = [];
    }
    updateSelections(currentSelections, currentParentId, scope);
    event.stopPropagation();
  });

  const handleMultiplierChange = useCallback(
    (event) => {
      getProcessedRow({ ...row[index], multiplier: event?.target?.value });
      event.stopPropagation();
    },
    [getProcessedRow, row, index]
  );

  const handleControlClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const toggleAccordion = useCallback(() => {
    setExpand((prev) => !prev);
  }, []);

  const isSelected = currentSelections?.includes(parentId);

  const isRestrictedPog = useMemo(() => {
    if (ENTITY_TYPES.orders === entityType) {
      return has(restrictedPogs, scope)
        ? restrictedPogs[scope]?.includes(parentId)
        : false;
    }
    if (adjQtyError) {
      const isErrorItemPresent = subRows?.find((i) => i.id === adjQtyError);
      return !!isErrorItemPresent;
    }
    return adjQtyError;
  }, [entityType, restrictedPogs, adjQtyError, scope, parentId, subRows]);

  const isMultiplierDisabled = useMemo(() => {
    if (adjQtyError) {
      const isErrorItemPresent = subRows?.find((i) => i.id === adjQtyError);
      return !!isErrorItemPresent;
    }
    return false;
  }, [adjQtyError, subRows]);

  const selectionCount = useMemo(
    () => (currentSelections?.length > 0 ? currentSelections?.length - 1 : 0),
    [currentSelections]
  );

  const title = `${currentTitle} - ${row[index]?.footage} - (${selectionCount}/${subRows?.length})`;

  return (
    <div ref={rowRef}>
      <Accordion
        disableGutters
        expanded={expand}
        onChange={updateSize}
        TransitionProps={VAccordionTransitionProps}
        sx={VAccordionSx}
      >
        <AccordionSummary
          onClick={toggleAccordion}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${row[index].id}-content`}
          id={`${row[index].id}-header`}
          sx={VAccordionSummarySx}
        >
          <Box className="sections">
            <Checkbox
              key={isSelected}
              defaultChecked={isSelected}
              disabled={isRestrictedPog}
              onClick={handleAccordionRowSelection}
            />
            <Tooltip
              title={
                row[index]?.region
                  ? `Region${row[index]?.region}`
                  : "Region - N/A"
              }
            >
              <Typography variant="h6" sx={SubTitleSx}>
                {title}
              </Typography>
            </Tooltip>
          </Box>
          {entityType === ENTITY_TYPES.planogram && (
            <Box className="sections">
              <FormControl sx={MultiplierSx} size="small">
                <InputLabel>Multiplier</InputLabel>
                <Select
                  label="Multiplier"
                  MenuProps={MenuProps}
                  disabled={isMultiplierDisabled}
                  onChange={handleMultiplierChange}
                  onOpen={handleControlClick}
                  onClose={handleControlClick}
                  value={row[index]?.multiplier || ""}
                >
                  {multiplierOptions.map((option) => (
                    <MenuItem sx={DropdownSx} key={option} value={option}>
                      {multiplierMappings[option]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={PogTableWrapperSx}>
            <Typography variant="h6" component="subtitle2" sx={VSubTitleSx}>
              Planogram Information
            </Typography>
            <PogTable
              data={row[index]}
              scope={scope}
              parentId={parentId}
              getProcessedRow={getProcessedRow}
              serverSelections={serverSelections}
              updateSelections={updateSelections}
              retailerNotes={retailerNotes}
              plannerNotes={plannerNotes}
            />
          </Box>
          <Box sx={ItemTableWrapperSx}>
            <Box sx={ToolbarSx}>
              <Typography variant="h6" component="subtitle2" sx={VSubTitleSx}>
                Item Information ({selectionCount}/{subRows?.length})
              </Typography>
              <ExportButton body={subRows} selections={serverSelections} />
            </Box>
            <ItemTable
              scope={scope}
              parentId={parentId}
              data={subRows}
              getProcessedRow={getProcessedRow}
              serverSelections={serverSelections}
              updateSelections={updateSelections}
              retailerNotes={retailerNotes}
              plannerNotes={plannerNotes}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default memo(VirtualizedAccordionRow);
